import React, { useEffect, useState, useCallback, useRef } from "react";
import Header from "../Header";
import {
  searchEngines,
  initialKeywords,
  MEDIA_TYPE,
} from "../../../../../constants/segmentTools";
import MediaMasonry from "../../../../SegmentBuilder/Shared/MediaMasonry";
import InitialView from "../../../../SegmentBuilder/Shared/InitialView";
import EmptySearchContainer from "../../../../SegmentBuilder/Shared/EmptySearchContainer";
import {
  usePostMediaBrowserDataV2,
  useSaveExternalImage,
} from "../../../../../services/mutations";
import * as Styled from "./styles";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../../redux/selectors/segmentBuilderSelectors";

const ImageBrowser = ({ onConfirm = () => {}, type }) => {
  const { mutate, isLoading, data } = usePostMediaBrowserDataV2();
  const saveExternalImageMutation = useSaveExternalImage();
  const wheelId = useSelector(getWheelId);
  const [selectedEngine, setSelectedEngine] = useState(
    searchEngines.image[0].key
  );
  const [selectedImage, setSelectedImage] = useState([]);
  const [imageKeyword, setImageKeyword] = useState("");
  const [isNotFound, setIsNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingImageItems, setLoadingImageItems] = useState(new Set());
  const containerRef = useRef(null);

  const SEARCH_LENGTH_LIMIT = 2;
  useEffect(() => {
    if (data?.data?.data) {
      setIsNotFound(!isLoading && data?.data?.data.length === 0);
      if (data?.data?.data.length > 0) {
        setSelectedImage((prevImages) => [
          ...prevImages,
          ...data?.data?.data.map(
            (d) => d.largeImageURL || d.original || d.url
          ),
        ]);
      }
    }
  }, [data, isLoading]);

  const getImages = (
    text = "",
    source = selectedEngine,
    page = 1,
    per_page = 20
  ) => {
    mutate({ text, type: MEDIA_TYPE.image, source, page, per_page });
  };

  const resetState = () => {
    setPage(1);
    setSelectedImage([]);
    setIsNotFound(false);
  };
  const handleImageKeywordChange = (val) => {
    setImageKeyword(val);
    resetState();
    if (val.length > SEARCH_LENGTH_LIMIT) getImages(val);
  };

  const handleEngineChange = (val) => {
    setSelectedEngine(val);
    resetState();
    if (imageKeyword.length > SEARCH_LENGTH_LIMIT) getImages(imageKeyword, val);
  };

  const handleSearch = () => {
    // Trigger a search using current keyword and engine
    resetState();
    if (imageKeyword.length > SEARCH_LENGTH_LIMIT) getImages(imageKeyword);
  };

  const handleInputChange = (val) => {
    setImageKeyword(val);
    if (!val) {
      // If cleared, also clear stored media data and reset paging
      resetState();
    }
  };

  const shouldDisplayInitialView =
    !imageKeyword || imageKeyword.length <= SEARCH_LENGTH_LIMIT;

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight + 100 >= scrollHeight) {
        if (!isLoading && !isNotFound) {
          const nextPage = page + 1;
          setPage(nextPage);
          getImages(imageKeyword, selectedEngine, nextPage);
        }
      }
    }
  }, [imageKeyword, selectedEngine, page, isLoading, isNotFound]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleErrorImage = (errorImage) => {
    setSelectedImage(selectedImage.filter((image) => image !== errorImage));
  };

  const handleSave = async (data) => {
    const imageUrl = data.src;

    // Add image item to loading set
    setLoadingImageItems((prev) => new Set([...prev, imageUrl]));

    try {
      let finalData = { ...data };

      // Save the external image to get a persisted URL
      const saveResponse = await saveExternalImageMutation.mutateAsync({
        url: imageUrl,
        wheel_id: wheelId,
      });

      // Use the URL returned from the API
      finalData.src = saveResponse?.data?.data?.url;

      // Continue with the normal flow
      onConfirm(finalData);
    } catch (error) {
      console.error("Error saving image:", error);
      // Still call onConfirm with original data if API call fails
      onConfirm(data);
    } finally {
      // Remove image item from loading set
      setLoadingImageItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(imageUrl);
        return newSet;
      });
    }
  };
  return (
    <Styled.Container width="95%">
      <Header
        selectedEngine={selectedEngine}
        handleEngineChange={handleEngineChange}
        handleChangeImageKeyword={handleInputChange}
        items={searchEngines.image}
        imageKeyword={imageKeyword}
        onSearch={handleSearch}
      />
      <Styled.MediaContainer ref={containerRef}>
        {shouldDisplayInitialView ? (
          <InitialView
            content={initialKeywords}
            handleSelectKeyword={handleImageKeywordChange}
          />
        ) : isNotFound ? (
          <EmptySearchContainer />
        ) : (
          <MediaMasonry
            content={selectedImage}
            imageKeyword={imageKeyword}
            isLoading={isLoading}
            onReplaceButtonPress={handleSave}
            handleErrorImage={handleErrorImage}
            type={type}
            buttonText={type === "images" ? "Select" : "Replace"}
            loadingImageItems={loadingImageItems}
          />
        )}
      </Styled.MediaContainer>
    </Styled.Container>
  );
};

export default ImageBrowser;
